import React, { useState, useEffect } from "react";
import { Button } from "@shared/ui/button";
import { EyeIcon, FilterX } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useRunReadings } from "@features/mx/meterReads/hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useChoices } from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { PageHeader } from "@shared/components/PageHeader";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface PhotoMeterFilters {
  status: string;
  type: string;
  search: string;
}

const CycleReadingsTable: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFilter = searchParams.get("status") || "";
  const typeFilter = searchParams.get("type") || "";
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const currentPage = Number(searchParams.get("page")) || 1;

  const [pendingFilters, setPendingFilters] = useState<PhotoMeterFilters>({
    status: decodeURIComponent(statusFilter),
    type: decodeURIComponent(typeFilter),
    search: searchQuery,
  });

  useEffect(() => {
    setPendingFilters({
      status: decodeURIComponent(statusFilter),
      type: decodeURIComponent(typeFilter),
      search: searchQuery,
    });
  }, [statusFilter, typeFilter]);

  const handlePendingFilterChange = (
    key: keyof PhotoMeterFilters,
    value: string
  ) => setPendingFilters((prev) => ({ ...prev, [key]: value }));

  const remoteUtilityId = useRemoteUtilityId();
  const pageSize = PAGE_SIZE;

  // --- ✅ new hook usage
  const { data: meterRunData, isLoading } = useRunReadings(
    remoteUtilityId,
    id!,
    statusFilter || undefined,
    typeFilter || undefined,
    searchQuery || undefined,
    currentPage
  );

  const { data: runStatus } = useChoices({ choice: "rule_status" });
  const { data: meterTypes } = useChoices({ choice: "meter_type" });
  const runStatusList = mapToLabelValue(runStatus);
  const meterTypeOptions = mapToLabelValue(meterTypes);
  // --- handlers
  const handleApplyFilters = () => {
    const newParams: any = {
      ...(pendingFilters.status && { status: pendingFilters.status }),
      ...(pendingFilters.type && { type: pendingFilters.type }),
      ...(pendingFilters.search && { search: pendingFilters.search }),
      tab: "reading",
      page: 1,
    };
    setSearchParams(newParams);
  };
  const handleSearch = (term: string) => {
    setPendingFilters((prev) => ({ ...prev, search: term }));
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      search: term,
    });
  };
  const resetFilters = () => setSearchParams({ page: "1", tab: "reading" });

  const handlePageChange = (p: number) =>
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      page: String(p),
    });

  const tabToRuleStatusMap: Record<string, string> = {
    pass: "PASS",
    "pending-readings": "NEW",
    exemptions: "FAIL",
    revisit: "REVISIT",
    "new-readings": "METER",
  };

  // ruleStatus → tab name (for navigation)
  const ruleStatusToTabMap: Record<string, string> = Object.fromEntries(
    Object.entries(tabToRuleStatusMap).map(([tab, status]) => [
      status.toLowerCase(),
      tab,
    ])
  );

  const handleViewCycle = (row) => {
    const ids = row?.validatorIds;
    const currentTab =
      ruleStatusToTabMap[row.ruleStatus?.toLowerCase()] || "pending-readings";

    navigate(
      `/mx/meter-validation/photometer-validation/${
        row.jobCard?.meterRun?.id
      }?tab=${currentTab}&page=1&readingId=${row.id}&validatorIds=${ids.join(
        ","
      )}`
    );
  };

  // --- columns
  const columns: TableColumn[] = [
    {
      key: "meterNumber",
      header: "Meter Number",
      type: "custom",
      render: (_, row) => row.meter.meterNumber,
      headerTooltip:
        "Unique identifier for the physical meter. Used to track meter history and link to consumer accounts.",
    },
    {
      key: "deviceNo",
      header: "Device Number",
      type: "custom",
      render: (_, row) => row?.meter?.deviceNo,
      headerTooltip:
        "Device ID used by field staff for reading capture. Links meter to mobile app and photo submissions.",
    },
    {
      key: "consumer",
      header: "Consumer Name	",
      type: "custom",
      render: (_, row) => row?.jobCard?.consumerName ?? "NA",
      headerTooltip:
        "Name of the customer associated with this meter. Used for billing and customer service inquiries.",
    },
    {
      key: "currentReading",
      header: "Current Reading",
      type: "custom",
      render: (_, row) => row?.meter?.currentReading,
      headerTooltip:
        "Latest meter reading value captured in this cycle. Used to calculate consumption for billing.",
    },
    {
      key: "previousReading",
      header: "Previous Reading",
      type: "custom",
      render: (_, row) => row?.previousReading ?? "NA",
      headerTooltip:
        "Last recorded reading from previous cycle. Used as baseline to calculate consumption difference.",
    },
    {
      key: "consumption",
      header: "Consumption",
      type: "custom",
      render: (_, row) => row?.meter?.averageConsumption ?? "NA",
      headerTooltip:
        "Difference between current and previous reading. Negative values indicate potential meter issues or reading errors.",
    },
    {
      key: "readingStatus",
      header: "Reading Status",
      type: "custom",
      render: (_, row) => row?.meter?.readingStatus,
      headerTooltip:
        "Type of reading: Normal (standard read), Faulty (meter malfunction detected), RCNT (could not access meter).",
    },
    {
      key: "ruleStatus",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getClassName: getStatusBadgeClass,
      },
      headerTooltip:
        "Validation status: Pending (awaiting review), Pass (validated), Revisit (needs re-check), Exempted (validation rule applied), Missing (not submitted), Duplicate (already recorded), New (recently added).",
    },
  ];

  const actions: TableAction[] = [
    {
      label: "View Cycle",
      icon: <EyeIcon size={20} className="text-gray-500" />,
      onClick: (row) => handleViewCycle(row),
    },
  ];

  const totalItems = meterRunData?.count || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const displayedData = meterRunData?.results || [];

  // --- Filter panel
  const filterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <Select
            value={pendingFilters.status}
            onValueChange={(v) => handlePendingFilterChange("status", v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Status" />
            </SelectTrigger>
            <SelectContent>
              {runStatusList.map((s) => (
                <SelectItem key={s.value} value={String(s.value)}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Type</label>
          <Select
            value={pendingFilters.type}
            onValueChange={(v) => handlePendingFilterChange("type", v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Type" />
            </SelectTrigger>
            <SelectContent>
              {meterTypeOptions.map((s) => (
                <SelectItem key={s.value} value={String(s.value)}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply
        </Button>
        <Button variant="ghost" onClick={resetFilters}>
          <FilterX className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Readings"
        subtitle="View collected readings. Search and filter by status and type."
      />
      <GenericTable
        data={displayedData}
        columns={columns}
        rowKey="id"
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage: PAGE_SIZE,
          startItem: totalItems > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0,
          endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
        }}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by meter number..."
        actionsType="icons"
        actions={actions}
        filterPanel={filterPanel}
        loading={isLoading}
        emptyMessage="No readings found matching your criteria"
      />
    </div>
  );
};

export default CycleReadingsTable;
